// src/features/affiliate/admin/PartnersTab.tsx
// Admin: review applications, approve/suspend affiliates, change their tier.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliate, useAffiliateCommissionTier, useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { Select } from '@/components/Select';
import type { Affiliate, AffiliateStatus } from '@core/types/affiliate';
import { AffiliateStatusBadge } from '../badges';
import { errMessage, formatMoney, listParams } from '../format';

const STATUS_FILTERS: (AffiliateStatus | '')[] = ['', 'AFFILIATE_STATUS_PENDING', 'AFFILIATE_STATUS_ACTIVE', 'AFFILIATE_STATUS_SUSPENDED', 'AFFILIATE_STATUS_REJECTED', 'AFFILIATE_STATUS_BANNED'];

export const PartnersTab = observer(() => {
  const { t, locale } = useI18n();
  const store = useAffiliate();
  const tierStore = useAffiliateCommissionTier();

  const [status, setStatus] = useState<AffiliateStatus | ''>('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<Affiliate | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [tierTarget, setTierTarget] = useState<Affiliate | null>(null);
  const [tierChoice, setTierChoice] = useState('');

  const load = () => {
    setError(null);
    store
      .ListAffiliates(listParams({ limit: 100 }, { status: status || undefined, q: search || undefined }))
      .catch((e: unknown) => setError(errMessage(e, t('affiliate.admin.partners.loadError'))));
  };

  useEffect(() => {
    load();
    tierStore.ListCommissionTiers(listParams({ limit: 50 })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (a: Affiliate) => {
    if (!a.entity) return;
    setBusyId(a.entity.id);
    setError(null);
    try {
      await store.ApproveAffiliate(a.entity.id, { entity: a.entity });
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.partners.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const suspend = async () => {
    if (!suspendTarget?.entity) return;
    setBusyId(suspendTarget.entity.id);
    setError(null);
    try {
      await store.SuspendAffiliate(suspendTarget.entity.id, { entity: suspendTarget.entity, reason: suspendReason });
      setSuspendTarget(null);
      setSuspendReason('');
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.partners.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const changeTier = async () => {
    if (!tierTarget?.entity || !tierChoice) return;
    setBusyId(tierTarget.entity.id);
    setError(null);
    try {
      await store.ChangeAffiliateTier(tierTarget.entity.id, { affiliate_id: tierTarget.entity.id, tier_id: tierChoice });
      setTierTarget(null);
      setTierChoice('');
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.partners.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as AffiliateStatus | '')}
          className="w-48"
          options={STATUS_FILTERS.map((s) => ({ value: s, label: s ? t(`affiliate.status.${s.replace('AFFILIATE_STATUS_', '').toLowerCase()}`) : t('common.all') }))}
        />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder={t('affiliate.admin.partners.searchPlaceholder')} className="max-w-xs" />
        <Button size="sm" variant="secondary" onClick={load}>{t('common.search')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {store.isLoading && store.items.length === 0 && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!store.isLoading && store.items.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('affiliate.admin.partners.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((a) => (
          <div key={a.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{a.display_name}</span>
                <AffiliateStatusBadge status={a.status} />
              </div>
              <p className="text-sm text-muted-foreground">{a.email} · {formatMoney(a.available_balance, locale)} {t('affiliate.dashboard.availableBalance').toLowerCase()}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              {a.status === 'AFFILIATE_STATUS_PENDING' && (
                <Button size="sm" onClick={() => approve(a)} isLoading={busyId === a.entity?.id}>{t('affiliate.admin.partners.approve')}</Button>
              )}
              {a.status === 'AFFILIATE_STATUS_ACTIVE' && (
                <Button size="sm" variant="danger" onClick={() => setSuspendTarget(a)}>{t('affiliate.admin.partners.suspend')}</Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => { setTierTarget(a); setTierChoice(a.tier?.id ?? ''); }}>
                {t('affiliate.admin.partners.changeTier')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {suspendTarget && (
        <div className="rounded-md border border-border p-3">
          <p className="text-sm font-medium text-foreground">{t('affiliate.admin.partners.suspendTitle', { name: suspendTarget.display_name })}</p>
          <Input className="mt-2" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} placeholder={t('affiliate.admin.partners.suspendReasonPlaceholder')} />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="danger" onClick={suspend} isLoading={busyId === suspendTarget.entity?.id}>{t('affiliate.admin.partners.suspend')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setSuspendTarget(null)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}

      {tierTarget && (
        <div className="rounded-md border border-border p-3">
          <p className="text-sm font-medium text-foreground">{t('affiliate.admin.partners.changeTierTitle', { name: tierTarget.display_name })}</p>
          <Select
            className="mt-2 w-56"
            value={tierChoice}
            onChange={(e) => setTierChoice(e.target.value)}
            options={tierStore.items.map((tr) => ({ value: tr.entity?.id ?? '', label: tr.name }))}
          />
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={changeTier} isLoading={busyId === tierTarget.entity?.id} disabled={!tierChoice}>{t('common.save')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setTierTarget(null)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </div>
  );
});
